require('./RAAccordionDistributionTable.scss');

// @vendors
const React = require('react');
const { Fragment } = React;
const PropTypes = require('prop-types');
const classnames = require('classnames');

// @commons
const { Accordion } = require('commonsComponents/accordion');
const { AccordionHeader } = require('commonsComponents/accordionHeader');
const { AccordionBody } = require('commonsComponents/accordionBody');
const { OKTable } = require('commonsComponents/OKTable');
const { OKTableRow } = require('commonsComponents/OKTableRow');
const { OKTableCell } = require('commonsComponents/OKTableCell');
const OKTooltip = require('components/commons/OKTooltip/OKTooltip');
const {
    ComparativeBarComposed,
} = require('commonsComponents/comparativeBarComposed');
const RAOneLineSymbolAmount = require('../commons/RAOneLineSymbolAmount/RAOneLineSymbolAmount');
const { ContentBox } = require('commonsComponents/contentBox');
const { LoadingSection } = require('commonsComponents/loadingSection');
const { EmptyState } = require('commonsComponents/emptyState');

// @components
const FeatureTogglingRender = require('components/conditionalRender/featureTogglingRender');

// @utilities
const { FormattedText, formatText } = require('core/i18n').i18n;
const { formatAmount } = require('utilities/currencyHelper');
const {
    formatPercentage,
    formatWithoutRoundedPercentage,
} = require('../../utilities/RACurrencyHelper');
const RAAssetsHelper = require('../../utilities/RAAssetsHelper');

// @constants
const {
    ACTIVE_CLASS_MONEY_MARKET_ID,
    ACTIVE_CLASS_FIXED_INCOME_ID,
    ACTIVE_CLASS_EQUITY_ID,
    ACTIVE_CLASS_REAL_ASSETS_ID,
    LIQUIDITY_SUBASSETCLASS_ID,
    SECONDARY_BAR_COLOR,
    EMPTY_ISIN,
    RA_LESS_THAN_PERCENT_VALUE,
} = require('../../raConstants/index');
const { ESG_PRODUCT } = require('constants/flagBasedFeatures');
const {
    EURO_SYMBOL,
    EURO_TEXT,
    PERCENTAGE_SYMBOL,
} = require('constants/index');

class RAAccordionDistributionTable extends React.Component {
    constructor(props) {
        super(props);
        this.handleAccordionLink = this.handleAccordionLink.bind(this);
    }
    getAssetFundsAmount(immSubAssetClasses) {
        const total = immSubAssetClasses
            .map(immSubAssetClass => immSubAssetClass.get('assets').size)
            .reduce((prev, next) => prev + next, 0);

        return total;
    }

    getWeightPercentText(weight) {
        let percentText;
        const percentWeightWithoutRounding = formatWithoutRoundedPercentage(
            weight,
            false,
        );
        if (
            percentWeightWithoutRounding >= 0 &&
            percentWeightWithoutRounding < RA_LESS_THAN_PERCENT_VALUE
        ) {
            percentText = formatText('broker-broker_lessThanPercent', [
                RA_LESS_THAN_PERCENT_VALUE,
            ]);
        } else {
            percentText = formatPercentage(weight);
        }
        return percentText;
    }

    getAccordionClassName(assetClassId, inactive = false) {
        const customClass = classnames(
            'ra-accordion-distribution-table__accordion',
            {
                'ra-accordion-distribution-table__accordion--money-market':
                    assetClassId === ACTIVE_CLASS_MONEY_MARKET_ID && !inactive,
                'ra-accordion-distribution-table__accordion--fixed-income':
                    assetClassId === ACTIVE_CLASS_FIXED_INCOME_ID && !inactive,
                'ra-accordion-distribution-table__accordion--equity':
                    assetClassId === ACTIVE_CLASS_EQUITY_ID && !inactive,
                'ra-accordion-distribution-table__accordion--real-assets':
                    assetClassId === ACTIVE_CLASS_REAL_ASSETS_ID && !inactive,
                'ra-accordion-distribution-table__accordion--money-market-inactive':
                    assetClassId === ACTIVE_CLASS_MONEY_MARKET_ID && inactive,
                'ra-accordion-distribution-table__accordion--fixed-income-inactive':
                    assetClassId === ACTIVE_CLASS_FIXED_INCOME_ID && inactive,
                'ra-accordion-distribution-table__accordion--equity-inactive':
                    assetClassId === ACTIVE_CLASS_EQUITY_ID && inactive,
                'ra-accordion-distribution-table__accordion--real-assets-inactive':
                    assetClassId === ACTIVE_CLASS_REAL_ASSETS_ID && inactive,
            },
        );

        return customClass;
    }

    buildLiquidityInfoRow() {
        return (
            <OKTableRow>
                <OKTableCell
                    colSpan={7}
                    className="ra-accordion-distribution-table__grid-cell--liquidity-info"
                >
                    <FormattedText
                        value="raAccordionDistributionTable-raAccordionDistributionTable_liquidityExplanatoryText"
                        className="ra-accordion-distribution-table__explanatory-text"
                    />
                </OKTableCell>
            </OKTableRow>
        );
    }

    buildLiquidityRow(barColor, immAsset, showRentabilityPercentage) {
        const formattedPercentage = formatPercentage(
            immAsset.get('weight'),
            false,
        );
        const rentability =
            immAsset.get('averagePurchasePriceBasedReturn') || 0;
        const liquidityInfoRow = __CONTACT_CENTER__
            ? this.buildLiquidityInfoRow()
            : null;
        const rowClassName = classnames({
            'ra-accordion-distribution-table__liqudity-row': __CONTACT_CENTER__,
        });

        let rentabilityBlock;
        if (showRentabilityPercentage) {
            rentabilityBlock = (
                <span>
                    {formatAmount(0)} {EURO_TEXT} | {formatAmount(rentability)}
                    {PERCENTAGE_SYMBOL}
                </span>
            );
        } else {
            rentabilityBlock = (
                <span>
                    {formatAmount(0)} {EURO_TEXT}
                </span>
            );
        }

        return (
            <React.Fragment>
                <OKTableRow className={rowClassName}>
                    <OKTableCell className="ra-accordion-distribution-table__grid-cell--first-row">
                        <FormattedText
                            value="raAccordionDistributionTable-liquidity"
                            className="ra-accordion-distribution-table__primary-text"
                        />
                        {!__CONTACT_CENTER__ && (
                            <OKTooltip
                                content="raAccordionDistributionTable-raAccordionDistributionTable_costDetailsTooltip"
                                offset={[0, -15]}
                            />
                        )}
                    </OKTableCell>
                    <FeatureTogglingRender notAllowedProductKey={ESG_PRODUCT}>
                        <OKTableCell className="ra-accordion-distribution-table__secondary-text" />
                    </FeatureTogglingRender>
                    <OKTableCell className="ra-accordion-distribution-table__grid-cell--middle">
                        <span className="ra-accordion-distribution-table__secondary-text">
                            -
                        </span>
                    </OKTableCell>
                    <OKTableCell className="ra-accordion-distribution-table__grid-cell--middle">
                        <span className="ra-accordion-distribution-table__secondary-text">
                            -
                        </span>
                    </OKTableCell>
                    <OKTableCell className="ra-accordion-distribution-table__grid-cell--middle">
                        <span className="ra-accordion-distribution-table__secondary-text">
                            -
                        </span>
                    </OKTableCell>
                    <OKTableCell className="ra-accordion-distribution-table__grid-cell--middle">
                        <span className="ra-accordion-distribution-table__secondary-text">
                            {formatAmount(immAsset.get('currentValuation'))}{' '}
                            {EURO_TEXT}
                        </span>
                    </OKTableCell>
                    <OKTableCell className="ra-accordion-distribution-table__grid-cell--middle ra-accordion-distribution-table__grid-cell--pre-last">
                        <span className="ra-accordion-distribution-table__secondary-text">
                            {rentabilityBlock}
                        </span>
                    </OKTableCell>
                    <OKTableCell className="ra-accordion-distribution-table__grid-cell--last">
                        <div className="ra-accordion-distribution-table__comparative-bar-wrap">
                            <span className="ra-accordion-distribution-table__percentage-text">
                                {this.getWeightPercentText(
                                    immAsset.get('weight'),
                                )}
                            </span>
                            <div className="ra-accordion-distribution-table__comparative-bar">
                                <ComparativeBarComposed
                                    firstValue={formattedPercentage}
                                    secondValue={100 - formattedPercentage}
                                    totalValue={100}
                                    firstColor={barColor}
                                    secondColor={SECONDARY_BAR_COLOR}
                                    height={10}
                                />
                            </div>
                        </div>
                    </OKTableCell>
                </OKTableRow>
                {liquidityInfoRow}
            </React.Fragment>
        );
    }

    buildAssetsRow(immSubAssetClass, assetClassId, barColor) {
        const { showRentabilityPercentage } = this.props;
        let immAssets, liquidityAssets;
        const subAssetClass = immSubAssetClass.get('subAssetClass');
        immAssets = immSubAssetClass.get('assets');
        immAssets.map((data, index) => {
            const isin = data.get('isin');
            if (isin === EMPTY_ISIN || isin === '') {
                liquidityAssets = data;
                immAssets = immAssets.remove(index);
            }
        });
        const sortedAssets = !!liquidityAssets
            ? immAssets.insert(0, liquidityAssets)
            : immAssets;
        const grid = sortedAssets.valueSeq().map(immAsset => {
            const isESG = immAsset.get('esg', false);
            const isin = !immAsset.get('isin')
                ? EMPTY_ISIN
                : immAsset.get('isin');
            const contributionAmount = formatAmount(
                immAsset.get('contributionAmount'),
                null,
                2,
            );
            const rentability = immAsset.get('averagePurchasePriceBasedReturn');
            let rentabilityBlock;
            const ESGTag = isESG ? (
                <i className="ra-accordion-distribution-table__esg-icon" />
            ) : (
                ''
            );

            if (
                assetClassId === ACTIVE_CLASS_MONEY_MARKET_ID &&
                isin === EMPTY_ISIN
            ) {
                return this.buildLiquidityRow(
                    barColor,
                    immAsset,
                    showRentabilityPercentage,
                );
            }

            if (showRentabilityPercentage) {
                rentabilityBlock = (
                    <div>
                        {formatAmount(contributionAmount)} {EURO_TEXT} | {''}
                        {formatAmount(
                            formatWithoutRoundedPercentage(rentability),
                        )}
                        {PERCENTAGE_SYMBOL}
                    </div>
                );
            } else {
                rentabilityBlock = (
                    <span>
                        {contributionAmount} {EURO_TEXT}
                    </span>
                );
            }

            return (
                <OKTableRow key={immAsset.get('subAssetClassId')}>
                    <OKTableCell className="ra-accordion-distribution-table__grid-cell--first">
                        <div className="ra-accordion-distribution-table__small-text">
                            {subAssetClass}
                        </div>
                        <div className="ra-accordion-distribution-table__item-text">
                            {immAsset.get('fundName')}
                        </div>
                        <div className="ra-accordion-distribution-table__small-text">
                            <FormattedText
                                value="broker-isinLabel"
                                injectedStrings={[isin]}
                            />
                        </div>
                    </OKTableCell>
                    <FeatureTogglingRender notAllowedProductKey={ESG_PRODUCT}>
                        <OKTableCell className="ra-accordion-distribution-table__grid-cell--middle">
                            {ESGTag}
                        </OKTableCell>
                    </FeatureTogglingRender>
                    <OKTableCell className="ra-accordion-distribution-table__grid-cell--middle">
                        <span className="ra-accordion-distribution-table__secondary-text">
                            {formatAmount(
                                immAsset.get('quantity'),
                                undefined,
                                3,
                            )}
                        </span>
                    </OKTableCell>
                    <OKTableCell className="ra-accordion-distribution-table__grid-cell--middle">
                        <span className="ra-accordion-distribution-table__secondary-text">
                            {formatAmount(immAsset.get('averagePurchasePrice'))}{' '}
                            {EURO_TEXT}
                        </span>
                    </OKTableCell>
                    <OKTableCell className="ra-accordion-distribution-table__grid-cell--middle">
                        <span className="ra-accordion-distribution-table__secondary-text">
                            {formatAmount(immAsset.get('netAssetValue'))}{' '}
                            {EURO_TEXT}
                        </span>
                    </OKTableCell>
                    <OKTableCell className="ra-accordion-distribution-table__grid-cell--middle">
                        <span className="ra-accordion-distribution-table__secondary-text">
                            {formatAmount(immAsset.get('currentValuation'))}{' '}
                            {EURO_TEXT}
                        </span>
                    </OKTableCell>
                    <OKTableCell className="ra-accordion-distribution-table__grid-cell--pre-last">
                        <span className="ra-accordion-distribution-table__secondary-text">
                            {rentabilityBlock}
                        </span>
                    </OKTableCell>
                    <OKTableCell className="ra-accordion-distribution-table__grid-cell--last">
                        <div className="ra-accordion-distribution-table__comparative-bar-wrap">
                            <span className="ra-accordion-distribution-table__percentage-text">
                                {this.getWeightPercentText(
                                    immAsset.get('weight'),
                                )}
                            </span>
                            <div className="ra-accordion-distribution-table__comparative-bar">
                                <ComparativeBarComposed
                                    firstValue={formatPercentage(
                                        immAsset.get('weight'),
                                        false,
                                    )}
                                    secondValue={
                                        100 -
                                        formatPercentage(
                                            immAsset.get('weight'),
                                            false,
                                        )
                                    }
                                    totalValue={100}
                                    firstColor={barColor}
                                    secondColor={SECONDARY_BAR_COLOR}
                                    height={10}
                                />
                            </div>
                        </div>
                    </OKTableCell>
                </OKTableRow>
            );
        });

        return grid;
    }

    buildSubAssetClassesGrid(immSubAssetClasses, assetClassId, barColor) {
        const { showRentabilityPercentage } = this.props;
        const grid = immSubAssetClasses
            .valueSeq()
            .map((immSubAssetClass, index) => {
                let row;
                if (
                    immSubAssetClass.get('subAssetClassId') ===
                    LIQUIDITY_SUBASSETCLASS_ID
                ) {
                    row = this.buildLiquidityRow(
                        barColor,
                        immSubAssetClass,
                        showRentabilityPercentage,
                    );
                } else {
                    row = this.buildAssetsRow(
                        immSubAssetClass,
                        assetClassId,
                        barColor,
                    );
                }

                return <Fragment key={index}>{row}</Fragment>;
            });

        return grid;
    }

    buildAccordionHeader(
        assetClassId,
        assetFundsAmount,
        currentValuation,
        expectedProfitability,
        contributionPercentage,
        showRentabilityPercentage,
    ) {
        const assetClassName = RAAssetsHelper.getAssetClassName(assetClassId);
        const rentabilityObject = {
            amount: expectedProfitability,
            currency: EURO_SYMBOL,
        };
        let rentabilityBlock;

        if (showRentabilityPercentage) {
            rentabilityBlock = (
                <div>
                    <RAOneLineSymbolAmount amountObject={rentabilityObject} /> |{' '}
                    {''}
                    {formatAmount(
                        formatWithoutRoundedPercentage(contributionPercentage),
                    )}
                    {PERCENTAGE_SYMBOL}
                </div>
            );
        } else {
            rentabilityBlock = (
                <RAOneLineSymbolAmount amountObject={rentabilityObject} />
            );
        }

        return (
            <div className="ra-accordion-distribution-table__header-content">
                <div className="ra-accordion-distribution-table__header-col ra-accordion-distribution-table__header-col--first">
                    <span className="ra-accordion-distribution-table__header-primary-text">
                        {assetClassName}
                    </span>
                    <span className="ra-accordion-distribution-table__header-content-secondary">
                        ({assetFundsAmount})
                    </span>
                </div>
                <div className="ra-accordion-distribution-table__header-col ra-accordion-distribution-table__header-col--third">
                    <div className="ra-accordion-distribution-table__header-info-group">
                        <div className="ra-accordion-distribution-table__secondary-text">
                            <FormattedText value="raDistributionWidget-gatherProfitability" />
                        </div>
                        <div className="ra-accordion-distribution-table__primary-text ra-accordion-distribution-table__primary-text--bold">
                            {rentabilityBlock}
                        </div>
                    </div>
                </div>
                <div className="ra-accordion-distribution-table__header-col ra-accordion-distribution-table__header-col--last">
                    <div className="ra-accordion-distribution-table__header-info-group">
                        <div className="ra-accordion-distribution-table__secondary-text">
                            <FormattedText value="raDistributionWidget-currentValue" />
                        </div>
                        <div className="ra-accordion-distribution-table__primary-text ra-accordion-distribution-table__primary-text--bold">
                            <RAOneLineSymbolAmount
                                amountObject={{
                                    amount: currentValuation,
                                    currency: EURO_SYMBOL,
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    buildTableHeader() {
        return (
            <OKTableRow
                className="ra-accordion-distribution-table__grid-header"
                asHeader={true}
            >
                <OKTableCell
                    className="ra-accordion-distribution-table__grid-cell--first ra-accordion-distribution-table__grid-header-cell--col-1"
                    asHeader={true}
                >
                    <FormattedText
                        value="raAccordionDistributionTable-tableHeaderColumn1"
                        className="ra-accordion-distribution-table__header-content-primary"
                    />
                </OKTableCell>
                <FeatureTogglingRender notAllowedProductKey={ESG_PRODUCT}>
                    <OKTableCell className="ra-accordion-distribution-table__grid-header-cell--col-2" />
                </FeatureTogglingRender>
                <OKTableCell
                    className="ra-accordion-distribution-table__grid-header-cell--col-3"
                    asHeader={true}
                >
                    <FormattedText
                        value="raAccordionDistributionTable-tableHeaderColumn2"
                        className="ra-accordion-distribution-table__header-content-primary"
                    />
                </OKTableCell>
                <OKTableCell
                    className="ra-accordion-distribution-table__grid-header-cell--col-4"
                    asHeader={true}
                >
                    <FormattedText
                        value="raAccordionDistributionTable-tableHeaderColumn3"
                        className="ra-accordion-distribution-table__header-content-primary"
                    />
                </OKTableCell>
                <OKTableCell
                    className="ra-accordion-distribution-table__grid-header-cell--col-5"
                    asHeader={true}
                >
                    <FormattedText
                        value="raAccordionDistributionTable-tableHeaderColumn5"
                        className="ra-accordion-distribution-table__header-content-primary"
                    />
                </OKTableCell>
                <OKTableCell
                    className="ra-accordion-distribution-table__grid-header-cell--col-6"
                    asHeader={true}
                >
                    <FormattedText
                        value="raAccordionDistributionTable-tableHeaderColumn6"
                        className="ra-accordion-distribution-table__header-content-primary"
                    />
                </OKTableCell>
                <OKTableCell
                    className="ra-accordion-distribution-table__grid-header-cell--col-7"
                    asHeader={true}
                >
                    <FormattedText
                        value="raAccordionDistributionTable-tableHeaderColumn7"
                        className="ra-accordion-distribution-table__header-content-primary"
                    />
                    <OKTooltip
                        alternativeIcon={true}
                        content="raAccordionDistributionTable-raAccordionDistributionTable_profitabilityTooltip"
                        offset={[0, -15]}
                    />
                </OKTableCell>
                <OKTableCell
                    className="ra-accordion-distribution-table__grid-cell--last ra-accordion-distribution-table__grid-header-cell--col-8"
                    asHeader={true}
                >
                    <FormattedText
                        value="raAccordionDistributionTable-tableHeaderColumn8"
                        className="ra-accordion-distribution-table__header-content-primary"
                    />
                </OKTableCell>
            </OKTableRow>
        );
    }

    buildEmptySubAsset() {
        return (
            <div className="ra-accordion-distribution-table__empty-state-wrapper">
                <EmptyState
                    primaryText={formatText(
                        'raAccordionDistributionTable-raAccordionDistributionTable_emptyStateMessage',
                    )}
                    iconType="noitems-wealth"
                />
            </div>
        );
    }

    buildSubAsset(tableHeader, subAssetClassesGrid) {
        return (
            <div>
                <thead>{tableHeader}</thead>
                <tbody>{subAssetClassesGrid}</tbody>
            </div>
        );
    }

    handleAccordionLink(state, id) {
        const assetClassName = RAAssetsHelper.getAssetClassName(id);
        this.props.onAccordionRecordLinkClick(assetClassName);
    }

    buildAssetClassAccordion(assetClassId, immAssetAllocations) {
        const { showRentabilityPercentage } = this.props;
        const immAssetAllocation = immAssetAllocations.find(
            immAssetClass => immAssetClass.get('assetClassId') === assetClassId,
        );

        const tableHeader = this.buildTableHeader();
        const barColor = RAAssetsHelper.getBarColor(assetClassId);
        const accordionClassName = this.getAccordionClassName(assetClassId);

        let assetFundsAmount = 0;
        let currentValuation = 0;
        let expectedProfitability = 0;
        let contributionPercentage = 0;
        let subAssets;
        if (!!immAssetAllocation) {
            const immSubAssetClasses = immAssetAllocation.get(
                'subAssetAllocations',
            );
            const subAssetClassesGrid = this.buildSubAssetClassesGrid(
                immSubAssetClasses,
                assetClassId,
                barColor,
            );
            assetFundsAmount = this.getAssetFundsAmount(immSubAssetClasses);
            currentValuation = immAssetAllocation.get('currentValuation');
            expectedProfitability = immAssetAllocation.get(
                'contributionAmount',
            );
            contributionPercentage = immAssetAllocation.get(
                'contributionPercentage',
            );
            subAssets = this.buildSubAsset(tableHeader, subAssetClassesGrid);
        } else {
            subAssets = this.buildEmptySubAsset();
        }

        const accordionHeader = this.buildAccordionHeader(
            assetClassId,
            assetFundsAmount,
            currentValuation,
            expectedProfitability,
            contributionPercentage,
            showRentabilityPercentage,
        );

        return (
            <Accordion
                customClassName={accordionClassName}
                id={assetClassId}
                key={assetClassId}
                onClick={this.handleAccordionLink}
            >
                <AccordionHeader name="AccordionHeader">
                    {accordionHeader}
                </AccordionHeader>
                <AccordionBody name="AccordionBody">
                    <div className="ra-accordion-distribution-table__accordion-body">
                        <OKTable className="ra-accordion-distribution-table__grid">
                            {subAssets}
                        </OKTable>
                    </div>
                </AccordionBody>
            </Accordion>
        );
    }

    buildDataTable(immAssetAllocations) {
        const moneyMarketAccordion = this.buildAssetClassAccordion(
            ACTIVE_CLASS_MONEY_MARKET_ID,
            immAssetAllocations,
        );
        const fixedIncomeAccordion = this.buildAssetClassAccordion(
            ACTIVE_CLASS_FIXED_INCOME_ID,
            immAssetAllocations,
        );
        const equityAccordion = this.buildAssetClassAccordion(
            ACTIVE_CLASS_EQUITY_ID,
            immAssetAllocations,
        );
        const realAssetsAccordion = this.buildAssetClassAccordion(
            ACTIVE_CLASS_REAL_ASSETS_ID,
            immAssetAllocations,
        );

        return (
            <Fragment>
                {moneyMarketAccordion}
                {fixedIncomeAccordion}
                {equityAccordion}
                {realAssetsAccordion}
            </Fragment>
        );
    }

    buildEmptyTable() {
        const emptyStateAssets = RAAssetsHelper.getEmptyStateAssets();
        const table = emptyStateAssets.map(asset => (
            <Accordion
                customClassName={this.getAccordionClassName(
                    asset.assetClassId,
                    true,
                )}
                key={asset.assetClassId}
            >
                <AccordionHeader name="AccordionHeader" canBeExpanded={false}>
                    <div className="ra-accordion-distribution-table__header-content ra-accordion-distribution-table__header-content--inactive">
                        <div className="ra-accordion-distribution-table__header-col ra-accordion-distribution-table__header-col--first">
                            <span className="ra-accordion-distribution-table__primary-text ra-accordion-distribution-table__primary-text--inactive">
                                {asset.assetClass}
                            </span>
                            <span className="ra-accordion-distribution-table__header-content-secondary ra-accordion-distribution-table__header-content-secondary--inactive">
                                (0)
                            </span>
                        </div>
                        <div className="ra-accordion-distribution-table__header-col ra-accordion-distribution-table__header-col--third">
                            <div className="ra-accordion-distribution-table__header-info-group">
                                <div className="ra-accordion-distribution-table__secondary-text">
                                    <FormattedText value="raDistributionWidget-gatherProfitability" />
                                </div>
                                <div className="ra-accordion-distribution-table__primary-text ra-accordion-distribution-table__primary-text--bold">
                                    0,00 €
                                </div>
                            </div>
                        </div>
                        <div className="ra-accordion-distribution-table__header-col ra-accordion-distribution-table__header-col--last">
                            <div className="ra-accordion-distribution-table__header-info-group">
                                <div className="ra-accordion-distribution-table__secondary-text">
                                    <FormattedText value="raDistributionWidget-currentValue" />
                                </div>
                                <div className="ra-accordion-distribution-table__primary-text ra-accordion-distribution-table__primary-text--bold">
                                    0,00 €
                                </div>
                            </div>
                        </div>
                        <i className="icon-accordion-abrir ok-collapsing-toggler__toggler ra-accordion-distribution-table__header-toggle-inactive" />
                    </div>
                </AccordionHeader>
                <AccordionBody name="AccordionBody" />
            </Accordion>
        ));

        return table;
    }

    buildTableFooter(
        immAssetAllocations,
        immKeyFigures,
        showRentabilityPercentage,
        scroll,
    ) {
        const netReturnObject = {
            amount: immKeyFigures.get('netReturn'),
            currency: EURO_SYMBOL,
        };
        const totalCurrentValuationObject = {
            amount: immKeyFigures.get('valuation'),
            currency: EURO_SYMBOL,
        };
        const footerClassName = classnames(
            'ra-accordion-distribution-table__footer',
            {
                'ra-accordion-distribution-table__footer--inactive': immAssetAllocations.isEmpty(),
                'ra-accordion-distribution-table__footer--scroll': scroll,
            },
        );
        let netReturnBlock;
        if (showRentabilityPercentage) {
            netReturnBlock = (
                <div>
                    <RAOneLineSymbolAmount amountObject={netReturnObject} /> |{' '}
                    {''}
                    {formatAmount(
                        formatWithoutRoundedPercentage(
                            immKeyFigures.get('timeWeightedReturn'),
                        ),
                    )}
                    {PERCENTAGE_SYMBOL}
                </div>
            );
        } else {
            netReturnBlock = (
                <RAOneLineSymbolAmount amountObject={netReturnObject} />
            );
        }

        return (
            <div className={footerClassName}>
                <div className="ra-accordion-distribution-table__footer-col ra-accordion-distribution-table__footer-col--first">
                    <div className="ra-accordion-distribution-table__footer-secondary-text">
                        <FormattedText value="raAccordionDistributionTable-totalInvestments" />
                    </div>
                </div>
                <div className="ra-accordion-distribution-table__footer-col ra-accordion-distribution-table__footer-col--second">
                    <div className="ra-accordion-distribution-table__footer-info-group">
                        <div className="ra-accordion-distribution-table__footer-secondary-text">
                            <FormattedText value="raDistributionWidget-gatherProfitability" />
                        </div>
                        <div className="ra-accordion-distribution-table__footer-primary-text">
                            {netReturnBlock}
                        </div>
                    </div>
                </div>
                <div className="ra-accordion-distribution-table__footer-col ra-accordion-distribution-table__footer-col--last">
                    <div className="ra-accordion-distribution-table__footer-secondary-text">
                        <FormattedText value="raDistributionWidget-currentValue" />
                    </div>
                    <div className="ra-accordion-distribution-table__footer-primary-text">
                        <RAOneLineSymbolAmount
                            amountObject={totalCurrentValuationObject}
                        />
                    </div>
                </div>
            </div>
        );
    }

    buildLoadingState() {
        return (
            <ContentBox customClassName="ra-accordion-distribution-table__loading-wrapper">
                <LoadingSection />
            </ContentBox>
        );
    }

    buildDistributionTable(
        immAssetAllocations,
        immKeyFigures,
        scroll,
        showRentabilityPercentage,
    ) {
        const tableFooter = this.buildTableFooter(
            immAssetAllocations,
            immKeyFigures,
            showRentabilityPercentage,
            scroll,
        );
        const tableBodyClassName = classnames(
            'ra-accordion-distribution-table__body',
            {
                'ra-accordion-distribution-table__body--scroll': scroll,
            },
        );
        let table;

        // @TODO: change this validation when service bring that user have hired 'Gestion Discrecional'
        if (immAssetAllocations && !immAssetAllocations.isEmpty()) {
            table = this.buildDataTable(immAssetAllocations);
        } else {
            table = this.buildEmptyTable();
        }

        return (
            <Fragment>
                <div className={tableBodyClassName}>{table}</div>
                {tableFooter}
            </Fragment>
        );
    }

    render() {
        const {
            immAssetAllocations,
            immKeyFigures,
            isFetching,
            scroll,
            showRentabilityPercentage,
        } = this.props;
        let content;

        if (isFetching) {
            content = this.buildLoadingState();
        } else {
            content = this.buildDistributionTable(
                immAssetAllocations,
                immKeyFigures,
                scroll,
                showRentabilityPercentage,
            );
        }

        return <div className="ra-accordion-distribution-table">{content}</div>;
    }
}

RAAccordionDistributionTable.propTypes = {
    immAssetAllocations: PropTypes.object,
    immKeyFigures: PropTypes.object,
    isFetching: PropTypes.bool,
    onAccordionRecordLinkClick: PropTypes.func,
    scroll: PropTypes.bool,
    showRentabilityPercentage: PropTypes.bool,
};

RAAccordionDistributionTable.defaultProps = {
    onAccordionRecordLinkClick: () => {},
    scroll: false,
    showRentabilityPercentage: false,
};

module.exports = RAAccordionDistributionTable;
